import { fetchCurrentHoldingVT2 } from 'views/Portfolio/components/Details/services/fetchCurrentHoldings';
import { fetchCellarStats } from 'views/MyCellar/services/fetchMyCellar';
import { ToolExecutionResult, CellarStatsData } from '../types';
import { formatCurrency } from '../utils/formatters';

export const executeGetCellarStats = async (): Promise<ToolExecutionResult> => {
  try {
    // Fetch stats from dedicated API for accurate totals
    // Use portfolioId=-1 to get stats for all portfolios
    const [statsResponse, holdingsResponse] = await Promise.all([fetchCellarStats(-1), fetchCurrentHoldingVT2()]);

    const apiStats = statsResponse;
    const products = holdingsResponse.data || [];

    if (apiStats.totalBottle === 0 && products.length === 0) {
      return {
        success: true,
        data: {
          message: 'No wines found in cellar',
          stats: null,
        },
      };
    }

    // Use API stats for accurate totals
    const totalBottles = apiStats.totalBottle;
    const totalCases = apiStats.totalCases;
    const totalValue = apiStats.totalValue;
    const uniqueWines = apiStats.uniqueLwin18 ?? products.length;

    // Calculate additional stats from holdings data
    const totalCost = products.reduce((sum, p) => sum + (p.totalCost || 0), 0);

    // Calculate average wine value and cost
    const avgWineValue = uniqueWines > 0 ? totalValue / uniqueWines : 0;
    const avgWineCost = uniqueWines > 0 ? totalCost / uniqueWines : 0;

    // Find highest and lowest valued wines
    const sortedByValue = [...products].sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
    const highestValuedWine = sortedByValue[0];
    const lowestValuedWine = sortedByValue[sortedByValue.length - 1];

    // Calculate vintage distribution
    const vintageDistribution: Record<string, number> = {};
    products.forEach((p) => {
      if (p.vintage) {
        const vintageNum = parseInt(p.vintage, 10);
        if (!Number.isNaN(vintageNum)) {
          const decade = `${Math.floor(vintageNum / 10) * 10}s`;
          vintageDistribution[decade] = (vintageDistribution[decade] || 0) + 1;
        }
      }
    });

    const stats: CellarStatsData = {
      totalBottles,
      totalCases,
      totalValue,
      uniqueWines,
    };

    // Get region stats from API
    const regionStats = apiStats.regionStats || [];

    return {
      success: true,
      data: {
        stats: {
          totalBottles: stats.totalBottles,
          totalCases: stats.totalCases,
          totalValue: formatCurrency(stats.totalValue),
          totalCost: formatCurrency(totalCost),
          uniqueWines: stats.uniqueWines,
          averageWineValue: formatCurrency(avgWineValue),
          averageWineCost: formatCurrency(avgWineCost),
        },
        regionBreakdown: regionStats.map((r) => ({
          region: r.region,
          count: r.totalCount,
        })),
        highlights: {
          highestValuedWine: highestValuedWine
            ? {
                name: `${highestValuedWine.wineName}${
                  highestValuedWine.vintage ? ` ${highestValuedWine.vintage}` : ''
                }`,
                value: formatCurrency(highestValuedWine.totalValue || 0),
              }
            : null,
          lowestValuedWine: lowestValuedWine
            ? {
                name: `${lowestValuedWine.wineName}${lowestValuedWine.vintage ? ` ${lowestValuedWine.vintage}` : ''}`,
                value: formatCurrency(lowestValuedWine.totalValue || 0),
              }
            : null,
        },
        vintageDistribution,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch cellar stats: ${(error as Error).message}`,
    };
  }
};
